import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  MapPin, 
  Navigation, 
  Settings, 
  CheckCircle, 
  Clock, 
  ChevronRight,
  ArrowLeft,
  Phone,
  MessageSquare,
  DollarSign,
  Package,
  Car,
  MapPinned,
  RefreshCw,
  Calendar,
  User,
  Bell,
  Smartphone
} from "lucide-react";
import { openMaps, getMapsProviderName, MapsProvider } from "@/lib/mapsUtils";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { NotificationProvider, useNotifications } from "@/contexts/NotificationContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { TenantProvider } from "@/contexts/TenantContext";
import NotificationBanner from "@/components/NotificationBanner";
import DriverJobOfferSheet from "@/components/DriverJobOfferSheet";
import Schedule from "@/pages/Schedule";
import AdminQuickBook from "@/pages/AdminQuickBook";
import Bookings from "@/pages/Bookings";
import CustomerBooking from "@/pages/CustomerBooking";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import AdminRescue from "@/pages/AdminRescue";
import useInAppBookingAlerts from "@/hooks/useInAppBookingAlerts";
import CustomerRegister from "@/pages/CustomerRegister";
import DriverRegister from "@/pages/DriverRegister";
import DriverProfile from "@/pages/DriverProfile";
import PaymentSuccess from "@/pages/PaymentSuccess";
import DriverEarnings from "@/pages/DriverEarnings";
import TrackingPage from "@/pages/TrackingPage";
import Account from "@/pages/Account";
import Plans from "@/pages/Plans";
import BecomeDriver from "@/pages/BecomeDriver";
import InstallApp from "@/pages/InstallApp";
import TaxSummaryPrint from "@/pages/TaxSummaryPrint";
import FeaturesCatalog from "@/pages/FeaturesCatalog";
import Help from "@/pages/Help";
import FranchiseApply from "@/pages/FranchiseApply";
import Legal from "@/pages/Legal";
import PreviewBanner from "@/components/PreviewBanner";

import { API } from "@/lib/api";
import DriverBottomNav from "@/components/DriverBottomNav";
import PushReliabilityBanner from "@/components/PushReliabilityBanner";


// Dashboard Component
const Dashboard = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("available");

  const fetchJobs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (e) {
      console.error("Failed to fetch jobs:", e);
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  const seedJobs = async () => {
    try {
      await axios.post(`${API}/jobs/seed`);
      toast.success("Sample jobs created!");
      fetchJobs();
    } catch (e) {
      console.error("Failed to seed jobs:", e);
      toast.error("Failed to create sample jobs");
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const availableJobs = jobs.filter(j => j.status === "available");
  const activeJobs = jobs.filter(j => j.status === "active");

  const getStepTypeIcon = (type) => {
    switch (type) {
      case "pickup": return <Package className="w-4 h-4" />;
      case "stop": return <MapPinned className="w-4 h-4" />;
      case "dropoff": return <MapPin className="w-4 h-4" />;
      default: return <MapPin className="w-4 h-4" />;
    }
  };

  const JobCard = ({ job }) => {
    const currentStep = job.steps[job.current_step_index];
    const isActive = job.status === "active";
    
    return (
      <Card 
        className="job-card bg-zinc-900 border-zinc-800 cursor-pointer touch-feedback animate-fade-in stagger-item"
        onClick={() => navigate(`/driver/job/${job.id}`)}
        data-testid={`job-card-${job.id}`}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">{job.customer_name}</h3>
              <p className="text-sm text-zinc-500">{job.steps.length} stop{job.steps.length > 1 ? 's' : ''}</p>
            </div>
            <Badge 
              className={isActive 
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" 
                : "bg-zinc-800 text-zinc-400 border-zinc-700"
              }
              data-testid={`job-status-${job.id}`}
            >
              {isActive ? "Active" : "Available"}
            </Badge>
          </div>
          
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3">
              <div className="mt-1 p-1.5 rounded bg-amber-500/10">
                {getStepTypeIcon(currentStep?.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">
                  {isActive ? "Next Stop" : "First Stop"}
                </p>
                <p className="text-sm text-zinc-300 truncate">{currentStep?.location?.address}</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center justify-between pt-3 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-emerald-400">
              <DollarSign className="w-4 h-4" />
              <span className="font-semibold">${job.total_payout.toFixed(2)}</span>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-600" />
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-zinc-950 safe-area-bottom">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 mt-16">
        <div className="px-5 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Link Ride</h1>
            <p className="text-sm text-zinc-500">Rideshare & Delivery</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/driver/earnings")}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="earnings-button"
            >
              <DollarSign className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/driver/profile")}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="profile-button"
            >
              <User className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/driver/settings")}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="settings-button"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-5 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-zinc-900 border border-zinc-800 p-1 mb-6">
            <TabsTrigger 
              value="available" 
              className="flex-1 data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
              data-testid="tab-available"
            >
              Available ({availableJobs.length})
            </TabsTrigger>
            <TabsTrigger 
              value="active" 
              className="flex-1 data-[state=active]:bg-zinc-800 data-[state=active]:text-white"
              data-testid="tab-active"
            >
              Active ({activeJobs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="available" className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : availableJobs.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 mb-4">No available jobs</p>
                <Button 
                  onClick={seedJobs}
                  className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                  data-testid="seed-jobs-button"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Create Sample Jobs
                </Button>
              </div>
            ) : (
              availableJobs.map(job => <JobCard key={job.id} job={job} />)
            )}
          </TabsContent>

          <TabsContent value="active" className="space-y-4">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Loading...</div>
            ) : activeJobs.length === 0 ? (
              <div className="text-center py-12">
                <Car className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No active jobs</p>
                <p className="text-sm text-zinc-600 mt-2">Accept a job to get started</p>
              </div>
            ) : (
              activeJobs.map(job => <JobCard key={job.id} job={job} />)
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Job Detail Component
const JobDetail = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [job, setJob] = useState(null);
  const [settings, setSettings] = useState({ preferred_maps: MapsProvider.GOOGLE });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchJob = useCallback(async () => {
    try {
      const [jobRes, settingsRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}`),
        axios.get(`${API}/driver/settings`)
      ]);
      setJob(jobRes.data);
      setSettings(settingsRes.data);
    } catch (e) {
      console.error("Failed to fetch job:", e);
      toast.error("Failed to load job details");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  const acceptJob = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/accept`);
      setJob(response.data);
      toast.success("Job accepted!");
    } catch (e) {
      console.error("Failed to accept job:", e);
      toast.error("Failed to accept job");
    } finally {
      setActionLoading(false);
    }
  };

  const completeStep = async () => {
    setActionLoading(true);
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/complete-step`);
      setJob(response.data);
      if (response.data.status === "completed") {
        toast.success("Job completed! Great work!");
        setTimeout(() => navigate("/driver"), 1500);
      } else {
        toast.success("Step completed! Moving to next stop.");
      }
    } catch (e) {
      console.error("Failed to complete step:", e);
      toast.error("Failed to complete step");
    } finally {
      setActionLoading(false);
    }
  };

  const handleNavigate = () => {
    const currentStep = job.steps[job.current_step_index];
    if (currentStep) {
      openMaps(settings.preferred_maps, currentStep.location);
      toast.success(`Opening ${getMapsProviderName(settings.preferred_maps)}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-500">Loading...</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-500 mb-4">Job not found</p>
          <Button onClick={() => navigate("/driver")} variant="outline">Go Back</Button>
        </div>
      </div>
    );
  }

  const currentStep = job.steps[job.current_step_index];
  const isActive = job.status === "active";
  const isCompleted = job.status === "completed";

  const getStepIcon = (step, index) => {
    if (step.status === "completed") {
      return <CheckCircle className="w-5 h-5 text-emerald-400" />;
    }
    if (step.status === "current") {
      return (
        <div className="w-5 h-5 rounded-full bg-amber-500 animate-pulse-ring flex items-center justify-center">
          <div className="w-2 h-2 rounded-full bg-black" />
        </div>
      );
    }
    return (
      <div className="w-5 h-5 rounded-full border-2 border-zinc-700 flex items-center justify-center">
        <span className="text-xs text-zinc-600">{index + 1}</span>
      </div>
    );
  };

  const getStepLabel = (type) => {
    switch (type) {
      case "pickup": return "Pickup";
      case "stop": return "Stop";
      case "dropoff": return "Dropoff";
      default: return "Stop";
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 pb-32">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="px-5 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/driver")}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white">{job.customer_name}</h1>
            <p className="text-sm text-zinc-500">
              {isCompleted ? "Completed" : `Step ${job.current_step_index + 1} of ${job.steps.length}`}
            </p>
          </div>
          <Badge 
            className={
              isCompleted
                ? "bg-zinc-800 text-zinc-400 border-zinc-700"
                : isActive 
                  ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" 
                  : "bg-amber-500/10 text-amber-400 border-amber-500/20"
            }
          >
            {isCompleted ? "Completed" : isActive ? "Active" : "Available"}
          </Badge>
        </div>
      </header>

      {/* Customer Info */}
      <div className="px-5 py-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
              <span className="text-lg font-semibold text-white">
                {job.customer_name.charAt(0)}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-white">{job.customer_name}</p>
              {job.customer_phone && (
                <p className="text-sm text-zinc-500">{job.customer_phone}</p>
              )}
            </div>
          </div>
          {job.customer_phone && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => window.open(`tel:${job.customer_phone}`, '_self')}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
              data-testid="call-customer-button"
            >
              <Phone className="w-5 h-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Steps List */}
      <div className="px-5 py-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">Route Steps</h2>
        <div className="space-y-1">
          {job.steps.map((step, index) => (
            <div 
              key={step.id} 
              className={`relative ${index < job.steps.length - 1 ? 'pb-6' : ''}`}
              data-testid={`step-${index}`}
            >
              {/* Connector Line */}
              {index < job.steps.length - 1 && (
                <div className="absolute left-[9px] top-8 bottom-0 w-0.5 bg-zinc-800" />
              )}
              
              <div className={`flex items-start gap-4 p-3 rounded-lg ${
                step.status === "current" ? "bg-zinc-900 border border-zinc-800" : ""
              }`}>
                <div className="mt-0.5">
                  {getStepIcon(step, index)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold uppercase tracking-wider ${
                      step.status === "current" ? "text-amber-400" : 
                      step.status === "completed" ? "text-emerald-400" : "text-zinc-600"
                    }`}>
                      {getStepLabel(step.type)}
                    </span>
                    {step.status === "current" && (
                      <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0">
                        Current
                      </Badge>
                    )}
                  </div>
                  <p className={`text-sm ${
                    step.status === "completed" ? "text-zinc-600" : "text-zinc-300"
                  }`}>
                    {step.location.address}
                  </p>
                  {step.instructions && (
                    <p className="text-xs text-zinc-500 mt-1">
                      {step.instructions}
                    </p>
                  )}
                </div>
                
                {/* Quick navigate button for current step */}
                {step.status === "current" && isActive && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      openMaps(settings.preferred_maps, step.location);
                    }}
                    className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                    data-testid={`quick-navigate-${index}`}
                  >
                    <Navigation className="w-5 h-5" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payout Info */}
      <div className="px-5 pb-6">
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex items-center justify-between">
            <span className="text-sm text-zinc-400">Total Payout</span>
            <span className="text-xl font-bold text-emerald-400">${job.total_payout.toFixed(2)}</span>
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Actions */}
      {!isCompleted && (
        <div className="fixed bottom-0 left-0 right-0 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 p-5 safe-area-bottom">
          {!isActive ? (
            <Button
              onClick={acceptJob}
              disabled={actionLoading}
              className="w-full py-6 text-lg font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-400 text-black nav-button-glow"
              data-testid="accept-job-button"
            >
              {actionLoading ? "Accepting..." : "Accept Job"}
            </Button>
          ) : (
            <div className="space-y-3">
              <Button
                onClick={handleNavigate}
                className="w-full py-6 text-lg font-bold uppercase tracking-wider bg-amber-500 hover:bg-amber-400 text-black nav-button-glow"
                data-testid="navigate-button"
              >
                <Navigation className="w-5 h-5 mr-3" />
                Navigate to {getStepLabel(currentStep?.type)}
              </Button>
              <Button
                onClick={completeStep}
                disabled={actionLoading}
                variant="outline"
                className="w-full py-4 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-300"
                data-testid="complete-step-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {actionLoading ? "Completing..." : "Mark Step Complete"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Settings Component
const SettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ preferred_maps: MapsProvider.GOOGLE });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/driver/settings`);
        setSettings(response.data);
      } catch (e) {
        console.error("Failed to fetch settings:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const updateMapsPreference = async (provider) => {
    try {
      const response = await axios.put(`${API}/driver/settings`, {
        preferred_maps: provider
      });
      setSettings(response.data);
      toast.success(`Maps preference updated to ${getMapsProviderName(provider)}`);
    } catch (e) {
      console.error("Failed to update settings:", e);
      toast.error("Failed to update settings");
    }
  };

  const mapsOptions = [
    { id: MapsProvider.GOOGLE, name: "Google Maps", description: "Works on all devices" },
    { id: MapsProvider.APPLE, name: "Apple Maps", description: "Best for iOS devices" },
    { id: MapsProvider.WAZE, name: "Waze", description: "Real-time traffic & community reports" }
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800">
        <div className="px-5 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/driver")}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="settings-back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-semibold text-white">Settings</h1>
        </div>
      </header>

      <main className="px-5 py-6">
        {/* Push notification reliability hint — auto-detects browser/PWA state */}
        <div className="mb-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-3">
            Ride alerts (push notifications)
          </h2>
          <PushReliabilityBanner />
        </div>

        {/* Install to home screen — quick link to dedicated install page */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/install')}
            variant="outline"
            className="w-full justify-start border-[#0047FF]/40 bg-[#0047FF]/10 text-white hover:bg-[#0047FF]/20 hover:text-white"
            data-testid="install-link-button"
          >
            <Smartphone className="w-4 h-4 mr-2 text-[#0047FF]" />
            Install Link Ride on your home screen
          </Button>
        </div>

        {/* Maps Preference */}
        <div className="mb-8">
          <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-4">
            Preferred Maps App
          </h2>
          <p className="text-sm text-zinc-400 mb-4">
            Choose which maps app opens when you tap Navigate
          </p>
          
          {loading ? (
            <div className="text-center py-8 text-zinc-500">Loading...</div>
          ) : (
            <div className="space-y-2">
              {mapsOptions.map(option => (
                <button
                  key={option.id}
                  onClick={() => updateMapsPreference(option.id)}
                  className={`w-full p-4 rounded-lg border text-left transition-colors ${
                    settings.preferred_maps === option.id
                      ? "bg-amber-500/10 border-amber-500/30"
                      : "bg-zinc-900 border-zinc-800 hover:border-zinc-700"
                  }`}
                  data-testid={`maps-option-${option.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-medium ${
                        settings.preferred_maps === option.id ? "text-amber-400" : "text-white"
                      }`}>
                        {option.name}
                      </p>
                      <p className="text-sm text-zinc-500">{option.description}</p>
                    </div>
                    {settings.preferred_maps === option.id && (
                      <CheckCircle className="w-5 h-5 text-amber-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-zinc-400">How it works</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-zinc-500">
            <p>
              When you tap the Navigate button, it opens your selected maps app with 
              directions to your current stop. Switch back to this app anytime to 
              see your next destination and tap Navigate again.
            </p>
            <p className="mt-3">
              <strong className="text-amber-400">No API costs!</strong> This uses your 
              device's built-in maps app.
            </p>
          </CardContent>
        </Card>

        {/* Admin Link */}
        <div className="mt-8 pt-6 border-t border-zinc-800">
          <Button
            onClick={() => navigate("/driver/admin")}
            variant="outline"
            className="w-full border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="admin-button"
          >
            <Settings className="w-4 h-4 mr-2" />
            Admin Panel
          </Button>
        </div>
      </main>
      <DriverBottomNav />
    </div>
  );
};

// Service-worker → app navigation bridge.
// When a push notification is tapped on iOS PWA, the SW posts a NAVIGATE
// message with the target URL. We listen here and call React Router's
// navigate() so the app routes to the right page even when client.navigate()
// isn't supported by the browser.
const SwNavigateListener = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const handler = (event) => {
      if (event?.data?.type === "NAVIGATE" && typeof event.data.url === "string") {
        navigate(event.data.url);
      }
    };
    navigator.serviceWorker.addEventListener("message", handler);
    return () => navigator.serviceWorker.removeEventListener("message", handler);
  }, [navigate]);
  return null;
};

// Legacy email links: older deployed builds sent emails with URLs like
// /operator/booking/:id and /admin/booking/:id. We never want a customer or
// operator to land on a blank page from an email, so we permanently redirect
// these old shapes to the current /track or /driver/track routes.
const LegacyBookingRedirect = ({ target }) => {
  const { bookingId } = useParams();
  return <Navigate to={`${target}/${bookingId}`} replace />;
};

// In-app notification fallback — belt-and-suspenders channel that surfaces
// booking status changes as toasts + chimes while the app is open. Runs
// as a null-render mount so it can live inside AuthProvider + BrowserRouter.
const InAppAlertsMount = () => {
  useInAppBookingAlerts();
  return null;
};

// Main App
function App() {
  return (
    <div className="dark">
      <TenantProvider>
      <AuthProvider>
        <NotificationProvider>
          <BrowserRouter>
            <RealtimeProvider>
              <Toaster 
                position="bottom-center" 
                toastOptions={{
                  style: {
                    background: '#18181b',
                    color: '#fafafa',
                    border: '1px solid #27272a'
                  }
                }}
              />
              <NotificationBanner />
              <DriverJobOfferSheet />
              <PreviewBanner />
              <SwNavigateListener />
              <InAppAlertsMount />
              <Routes>
                {/* Customer App - Main site */}
                <Route path="/" element={<CustomerBooking />} />
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin-rescue" element={<AdminRescue />} />
                <Route path="/register/customer" element={<CustomerRegister />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/track/:bookingId" element={<TrackingPage />} />
                <Route path="/account" element={<Account />} />
                <Route path="/plans" element={<Plans />} />
                <Route path="/install" element={<InstallApp />} />
                <Route path="/features" element={<FeaturesCatalog />} />
                <Route path="/help" element={<Help />} />
                <Route path="/franchise" element={<FranchiseApply />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/legal/:docId" element={<Legal />} />
                <Route path="/admin/taxes/print" element={<TaxSummaryPrint />} />
                <Route path="/become-driver" element={<BecomeDriver />} />

                {/* Driver App */}
                <Route path="/driver" element={<Navigate to="/driver/bookings" replace />} />
                <Route path="/driver/register" element={<DriverRegister />} />
                <Route path="/driver/profile" element={<DriverProfile />} />
                <Route path="/driver/earnings" element={<DriverEarnings />} />
                <Route path="/driver/job/:jobId" element={<JobDetail />} />
                <Route path="/driver/settings" element={<SettingsPage />} />
                <Route path="/driver/schedule" element={<Schedule />} />
                <Route path="/driver/new-booking" element={<AdminQuickBook />} />
                <Route path="/driver/bookings" element={<Bookings />} />
                <Route path="/driver/track/:bookingId" element={<TrackingPage />} />
                <Route path="/driver/admin" element={<Admin />} />
                {/* Legacy email link redirects — older deployed builds emailed these URL
                    shapes; keep them mapped so historical emails always resolve. */}
                <Route path="/operator/booking/:bookingId" element={<LegacyBookingRedirect target="/driver/track" />} />
                <Route path="/admin/booking/:bookingId" element={<LegacyBookingRedirect target="/driver/track" />} />
                <Route path="/booking/:bookingId" element={<LegacyBookingRedirect target="/track" />} />
              </Routes>
            </RealtimeProvider>
          </BrowserRouter>
        </NotificationProvider>
      </AuthProvider>
      </TenantProvider>
    </div>
  );
}

export default App;
